/**
 * Avatar utilities — web port of
 * client-native/src/features/profile/utils/avatarUtils.ts (source of truth).
 *
 * Bird avatar keys, default-bird-by-name logic, and per-user localStorage keys
 * are kept EXACT. Custom images are stored on web as data URLs (iOS stores a
 * file URI). Storage keys match iOS MMKV keys so behaviour is identical.
 */
import blueBird from "@/assets/birdprofiles/blue-bird.png";
import blueEgg from "@/assets/birdprofiles/blue-egg.png";
import blueGoofy from "@/assets/birdprofiles/blue-goofy.png";
import yellowFunk from "@/assets/birdprofiles/yellow-funk.png";

export const BIRD_AVATARS = {
  "blue-bird": blueBird,
  "blue-egg": blueEgg,
  "blue-goofy": blueGoofy,
  "yellow-funk": yellowFunk,
} as const;

export type BirdAvatarKey = keyof typeof BIRD_AVATARS;
export type ProfileAvatarType = "bird" | "custom";

export const PROFILE_AVATAR_STORAGE_PREFIX = "profile_avatar_";

export const getProfileAvatarTypeKey = (userId: number | string) =>
  `${PROFILE_AVATAR_STORAGE_PREFIX}type_${userId}`;

export const getProfileAvatarValueKey = (userId: number | string) =>
  `${PROFILE_AVATAR_STORAGE_PREFIX}value_${userId}`;

/**
 * Default bird avatar based on the first letter of the user's name.
 *   A-F → blue-bird,  G-L → blue-egg,  M-S → blue-goofy,  T-Z → yellow-funk.
 */
export function getDefaultBirdForName(name: string | undefined): BirdAvatarKey {
  if (!name || name.length === 0) return "blue-bird";

  const charCode = name.charAt(0).toUpperCase().charCodeAt(0);
  if (charCode >= 65 && charCode <= 70) return "blue-bird"; // A-F
  if (charCode >= 71 && charCode <= 76) return "blue-egg"; // G-L
  if (charCode >= 77 && charCode <= 83) return "blue-goofy"; // M-S
  if (charCode >= 84 && charCode <= 90) return "yellow-funk"; // T-Z
  return "blue-bird";
}

/** Image URL for a bird avatar key. */
export function getBirdAvatarSource(birdKey: BirdAvatarKey): string {
  return BIRD_AVATARS[birdKey];
}

function isBirdAvatarKey(value: string): value is BirdAvatarKey {
  return value in BIRD_AVATARS;
}

/** A validated stored avatar choice (or null when nothing valid is stored). */
export type StoredAvatar =
  | { type: "bird"; value: BirdAvatarKey }
  | { type: "custom"; value: string };

/**
 * Read + normalize the user's stored avatar choice from per-user localStorage.
 * The SINGLE place that validates the stored type/value, so Profile and chat
 * handle stale/corrupt storage identically: an unknown type, a stale bird key,
 * or a non-data-URL custom value all return null → caller uses the default bird.
 */
export function readStoredAvatar(
  userId: number | string | undefined | null,
): StoredAvatar | null {
  if (userId == null) return null;
  const savedType = localStorage.getItem(getProfileAvatarTypeKey(userId));
  const savedValue = localStorage.getItem(getProfileAvatarValueKey(userId));
  if (!savedValue) return null;
  if (savedType === "bird" && isBirdAvatarKey(savedValue)) {
    return { type: "bird", value: savedValue };
  }
  if (savedType === "custom" && savedValue.startsWith("data:image/")) {
    return { type: "custom", value: savedValue };
  }
  return null;
}

/**
 * Resolve the learner's profile avatar image the SAME way the Profile screen
 * does: a saved custom photo (data URL) or chosen bird from per-user
 * localStorage, else the default bird derived from their first name. Use this
 * anywhere the learner's avatar appears (Profile, chat reply bubbles, …) so they
 * all show the user's real avatar instead of the empty server `avatarUrl`.
 */
export function resolveProfileAvatarSrc(
  userId: number | string | undefined | null,
  firstName: string | undefined,
): string {
  const stored = readStoredAvatar(userId);
  if (stored) {
    return stored.type === "bird"
      ? getBirdAvatarSource(stored.value)
      : stored.value;
  }
  return getBirdAvatarSource(getDefaultBirdForName(firstName));
}

/** All bird avatar keys (display order matches iOS). */
export function getAllBirdAvatarKeys(): BirdAvatarKey[] {
  return Object.keys(BIRD_AVATARS) as BirdAvatarKey[];
}
